import type { Module } from '@/types/course';

export const module4: Module = {
  slug: 'modulo-4-custodia-almacenamiento',
  number: 4,
  title: 'Custodia y almacenamiento',
  description:
    'Comprar bien es la mitad del trabajo; custodiar bien es la otra mitad. Una mala custodia puede convertir una buena inversión en una pesadilla.',
  durationMinutes: 55,
  badge: 'custodio-seguro',
  lessons: [
    {
      slug: 'caja-fuerte-casa',
      number: '4.1',
      title: 'Caja fuerte en casa',
      durationMinutes: 18,
      videoUrl: null,
      content: `Custodiar el oro en domicilio es la opción más extendida entre inversores patrimoniales pequeños y medios.

**Ventajas reales**:
- Acceso 24/7 sin depender de horarios.
- Discreción: nadie más conoce ubicación ni cantidad.
- Sin costes recurrentes (más allá del coste único de la caja y posibles seguros).
- Independencia de terceros.

**Riesgos a gestionar**:
- Robo: el más obvio.
- Incendio: la mayoría de cajas baratas no protegen frente a fuego prolongado.
- Pérdida de combinación o llave.
- Olvido por herederos sin registro adecuado.

**Tipología de cajas** (categorías UNE-EN 1143-1):

| Categoría | Resistencia | Capital recomendado |
|---|---|---|
| Grado 0 | Hasta 6.000€ | <5.000€ |
| Grado I | Hasta 10-15.000€ | 5-15.000€ |
| Grado II | Hasta 25-35.000€ | 15-50.000€ |
| Grado III | Hasta 50-100.000€ | 50-150.000€ |
| Grado IV+ | >100.000€ | 150.000€+ |

**Características técnicas a exigir**:
- Certificación UNE-EN 1143-1 (anti-rotura) y UNE-EN 1047-1 (resistencia al fuego).
- Anclaje al suelo o pared.
- Sistema de apertura redundante.
- Material grueso (hierro, hormigón armado, acero al manganeso).
- Bisagras internas (no externas).
- Resistencia al fuego mínimo 60 minutos a 1.000°C.

**Ubicación y discreción**:
- **No** la pongas a la vista. Empotrarla la oculta.
- **No** elijas el dormitorio principal (primer sitio que registra un ladrón).
- **No** la instales tú mismo si pesa >100 kg.
- **Sí** considera trampillas, falsos techos, suelos elevados.
- **Sí** mantén la existencia **fuera de redes sociales y conversaciones casuales**.

**Seguros**. Las pólizas de hogar estándar tienen coberturas limitadas:
- 1.500-3.000€ sin caja fuerte.
- 6.000-15.000€ con caja certificada y declarada.
- Cantidades superiores requieren **póliza específica de joyería/metales** con peritaje individual.

Para patrimonios significativos (>30.000€), una póliza específica suele costar 0,3-1% anual del valor asegurado.

**Registro patrimonial privado** (crítico para herencia):
- Existencia de la caja y ubicación.
- Combinación o instrucciones de apertura.
- Inventario actualizado.
- Facturas archivadas en lugar separado.

Sin este registro, hay riesgo real de que tus herederos no encuentren el oro o no puedan abrir la caja.`,
      keyPoints: [
        'Custodia en casa = acceso 24/7, discreción y cero costes recurrentes, pero exige caja adecuada y seguro.',
        'La caja debe estar certificada (UNE-EN 1143-1), anclada, oculta y con resistencia al fuego.',
        'Coberturas estándar de hogar son insuficientes para grandes patrimonios; póliza específica desde 30.000€+.',
        'Registro patrimonial privado para herederos es tan importante como la caja misma.',
        'Discreción radical: la caja invisible es la caja segura.',
      ],
    },
    {
      slug: 'cajas-bancarias-depositos-privados',
      number: '4.2',
      title: 'Cajas de seguridad bancarias y depósitos privados',
      durationMinutes: 18,
      videoUrl: null,
      content: `Las cajas de seguridad en bancos son la opción tradicional. Han perdido protagonismo en las últimas décadas, pero siguen siendo válidas con ciertos matices.

**Cajas de seguridad bancarias**:

**Ventajas**:
- Seguridad física institucional.
- Tarifas relativamente bajas (50-300€/año).
- Acceso bajo control identificado.

**Desventajas**:

1. **No están aseguradas por defecto**. El banco custodia el espacio, pero el contenido es responsabilidad del cliente. Indemnizaciones suelen estar limitadas a 3.000-12.000€.

2. **Acceso restringido a horarios bancarios**. No puedes acceder fines de semana, festivos, durante crisis financieras donde el banco cierre.

3. **Riesgo en crisis financiera**. En episodios graves (corralito argentino 2001, Grecia 2015, Chipre 2013), el acceso fue temporalmente bloqueado.

4. **Tendencia a la desaparición**. Varias entidades grandes han cerrado o reducido el servicio.

**Depósitos privados especializados**:

Empresas dedicadas exclusivamente a la custodia de metales preciosos. Ejemplos internacionales: Loomis, Brinks, Malca-Amit, BullionVault, GoldMoney.

**Modelos**:

- **Custodia segregada**: tu oro específico (con números de serie identificados) está en un compartimento individual a tu nombre. Es jurídicamente tuyo en todo momento. Coste: 0,5-1,5% anual.

- **Custodia agrupada (no segregada)**: tu oro entra en un pool común. Tienes derecho a una cantidad equivalente, pero no a piezas específicas. Más barato (0,1-0,5%) pero con riesgo en caso de problemas.

- **Custodia con asignación numérica**: variante intermedia.

**Ventajas de los depósitos privados profesionales**:
- Seguro completo incluido en la tarifa.
- Auditorías independientes regulares.
- Especialización exclusiva.
- Acceso por aviso (24-72h).
- Posibilidad de custodia internacional (Suiza, Singapur, Liechtenstein).

**Desventajas**:
- Coste recurrente significativo.
- Dependencia del custodio.
- Acceso menos inmediato.
- Custodia internacional implica Modelo 720 si patrimonio >50.000€ fuera de España.

**Comparativa**:

| Criterio | Caja casa | Caja banco | Depósito privado |
|---|---|---|---|
| Acceso | 24/7 | Horario bancario | Cita 24-72h |
| Coste | Cero | 50-300€/año | 0,5-1,5%/año |
| Seguro | Por separado | Limitado | Incluido |
| Riesgo robo | Medio | Bajo | Muy bajo |
| Riesgo crisis | Bajo | Medio | Bajo |
| Discreción | Alta | Media | Media-alta |
| Capital máx práctico | ~150k€ | ~500k€ | Sin límite |

**Recomendación de combinación** para patrimonios entre 50.000€ y 500.000€:
- **Caja fuerte en casa**: 20-30% (liquidez de emergencia).
- **Depósito privado segregado**: 70-80% (mayoría del patrimonio).

Para patrimonios mayores, considerar **diversificación geográfica internacional**.`,
      keyPoints: [
        'Cajas bancarias: tarifa baja pero seguro limitado y acceso restringido.',
        'Depósitos privados profesionales: seguro completo, especialización, pero coste recurrente del 0,5-1,5%.',
        'Custodia segregada (oro específicamente identificado) es preferible a no segregada.',
        'Depósito internacional implica Modelo 720 si patrimonio supera 50.000€ fuera de España.',
        'Combinación óptima: parte en casa + parte en depósito profesional.',
      ],
    },
    {
      slug: 'segregado-vs-no-segregado',
      number: '4.3',
      title: 'Almacenamiento profesional segregado vs no segregado',
      durationMinutes: 19,
      videoUrl: null,
      content: `Si has decidido confiar parte de tu oro a un custodio profesional, la siguiente decisión es **segregado vs no segregado**.

**Custodia segregada (allocated)**:

Tu oro **es tuyo**, jurídicamente, durante toda la custodia. Los lingotes específicos —identificados por número de serie, peso, refinería y pureza— están físicamente en un compartimento marcado con tu identificación. Si el custodio quiebra, esos lingotes no forman parte de la masa concursal: son tuyos.

**Características**:
- Identificación individual de cada pieza.
- Auditorías permiten verificar presencia física.
- Sin dilución entre clientes.
- Coste: 0,5-1,5% anual.

**Custodia no segregada / pooled / unallocated**:

Tu oro se mezcla con el de otros clientes en un pool común. El custodio tiene contabilidad de cuánto te corresponde, pero las piezas físicas no están individualmente identificadas. Tienes un derecho contable, no una propiedad sobre piezas específicas.

**Características**:
- Más barato (0,1-0,5% anual).
- Operativamente más simple para el custodio.
- Suele permitir compraventas más ágiles.
- **Riesgo crucial**: en caso de problemas operativos o quiebra, tus derechos son contables, no físicos. Podrías recuperar valor en efectivo, no oro físico.

**¿Por qué importa?**

En tiempos normales, ambas funcionan igual. La diferencia se materializa exactamente cuando más buscas seguridad: en una crisis sistémica grave o quiebra del custodio.

**Caso real**: en 2008, varios fondos de oro y custodios institucionales sufrieron presiones operativas. Los clientes con oro segregado recuperaron sus piezas físicas íntegras. Los clientes con custodia no segregada en algunas entidades sufrieron retrasos y, en casos puntuales, conversiones forzosas a efectivo a precios desfavorables.

**Cómo verificar segregación real**:
1. **Documento contractual** que mencione "allocated" o "segregada" con asignación específica.
2. **Lista de inventario individualizada** con números de serie.
3. **Auditorías independientes** por auditores reconocidos.
4. **Posibilidad de inspección** física por el cliente.
5. **Política clara de entrega** física en plazos razonables.

**Coste vs seguridad**:

Para patrimonios pequeños (<20.000€), el coste de custodia segregada del 0,5-1% anual puede ser significativo. La elección razonable es custodia en domicilio con seguro.

Para patrimonios medios y grandes (>20.000€), la custodia segregada profesional es **claramente superior**. La diferencia entre segregada y no segregada (a veces 0,3-0,5% anual extra) es uno de los mejores "seguros" disponibles.

**Custodia internacional**:

Algunos inversores diversifican geográficamente. Las jurisdicciones con tradición de custodia (Suiza, Liechtenstein, Singapur, Andorra) ofrecen estabilidad. Implicaciones:
- **Modelo 720** obligatorio si valor en el extranjero >50.000€.
- **Coste superior** (1-1,5% anual).
- **Acceso menos inmediato**.

> *Este contenido es educativo. La elección de custodia debe adaptarse a tu situación particular.*`,
      keyPoints: [
        'Custodia segregada = oro físicamente identificado y jurídicamente tuyo en todo momento.',
        'Custodia no segregada = derecho contable sobre un pool común, riesgo en caso de problemas.',
        'En crisis sistémicas, segregada protege; no segregada puede convertirte en acreedor concursal.',
        'Verifica segregación real: contrato explícito, inventario individualizado, auditorías independientes.',
        'Custodia internacional aporta diversificación geográfica pero implica Modelo 720.',
      ],
    },
  ],
  quiz: [
    {
      id: 'm4-q1',
      question:
        '¿Qué certificación europea garantiza la resistencia anti-rotura de una caja fuerte?',
      options: ['ISO 9001', 'UNE-EN 1143-1', 'UNE-EN 1047-1 (resistencia al fuego)', 'CE'],
      correctIndex: 1,
      explanation:
        'UNE-EN 1143-1 es la norma europea que clasifica las cajas fuertes por resistencia al ataque. UNE-EN 1047-1 es la norma de resistencia al fuego (complementaria, no equivalente).',
    },
    {
      id: 'm4-q2',
      question:
        'Si custodias 100.000€ en oro en casa, ¿qué tipo de seguro necesitas?',
      options: [
        'Solo el seguro de hogar estándar',
        'Ningún seguro adicional',
        'Una póliza específica de metales preciosos con peritaje',
        'Un seguro de coche',
      ],
      correctIndex: 2,
      explanation:
        'Las pólizas de hogar cubren típicamente 3.000-15.000€ en metales preciosos. Patrimonios mayores requieren póliza específica con declaración detallada del contenido.',
    },
    {
      id: 'm4-q3',
      question: '¿Qué diferencia hay entre custodia segregada y no segregada?',
      options: [
        'La segregada es más barata',
        'La segregada identifica físicamente tus piezas; la no segregada es un pool con derecho contable',
        'Solo difieren en el horario de acceso',
        'No hay diferencia legal real',
      ],
      correctIndex: 1,
      explanation:
        'La distinción es jurídicamente crítica. En segregada, las piezas son tuyas físicamente. En no segregada, eres acreedor del pool, lo que cambia tu posición en caso de quiebra del custodio.',
    },
    {
      id: 'm4-q4',
      question: '¿Cuándo es OBLIGATORIO presentar el Modelo 720?',
      options: [
        'Siempre que compres oro',
        'Si vendes oro con beneficio',
        'Si custodias más de 50.000€ en bienes en el extranjero',
        'Solo para criptomonedas',
      ],
      correctIndex: 2,
      explanation:
        'El Modelo 720 obliga a declarar bienes y derechos situados en el extranjero por valor superior a 50.000€ (cuentas, valores, inmuebles, metales preciosos custodiados fuera).',
    },
    {
      id: 'm4-q5',
      question:
        '¿Cuál es la combinación de custodia más recomendada para un patrimonio de 200.000€ en oro?',
      options: [
        'Todo en casa para máxima discreción',
        'Todo en banco por máxima seguridad',
        'Combinación: parte en casa (liquidez) + parte en depósito segregado',
        'Todo en custodia internacional',
      ],
      correctIndex: 2,
      explanation:
        'Para patrimonios significativos, la combinación caja en casa (20-30%) + depósito profesional segregado (70-80%) ofrece el mejor equilibrio entre liquidez, seguridad, coste y diversificación de riesgo.',
    },
  ],
};
